import { NmLoader } from './NmLoader';
import { useTranslation } from 'react-i18next';
import { displayDigits } from '../utils/displayDigits';
import React from 'react';
import { useAthleteMetrics } from '../hooks/useAthleteMetrics';


interface AthleteMetricsBarProps {
  userId: number | null;
  /** Optional athlete name shown before metrics. */
  athleteName?: string;
  compact?: boolean;
  /** Flat inline layout for embedding in a toolbar row (no card chrome). */
  inline?: boolean;
  /** Vertical label:value rows for embedding in cards (e.g. comparison workout slots). */
  stacked?: boolean;
  /** Bump to refetch after calendar/event mutations. */
  refreshToken?: number;
}

function MetricChip({
  label,
  value,
  unit,
  valueColor,
  compact,
}: {
  label: string;
  value: string;
  unit?: string;
  valueColor?: string;
  compact?: boolean;
}) {
  return (
    <div style={chipStyle}>
      <span style={{ ...chipLabel, fontSize: compact ? 9 : 10 }}>{label}</span>
      <span style={{ ...chipValue, fontSize: compact ? 11 : 13, color: valueColor ?? 'var(--fg-1)' }}>
        {displayDigits(value)}
        {unit && <span style={chipUnit}>{unit}</span>}
      </span>
    </div>
  );
}

function Sep({ inline, compact }: { inline?: boolean; compact?: boolean }) {
  return (
    <span
      style={inline ? headerSepInline : sepStyle}
      aria-hidden
    >
      {inline ? '|' : '·'}
    </span>
  );
}

function StackedRow({
  label,
  value,
  unit,
  valueColor,
}: {
  label: string;
  value: string;
  unit?: string;
  valueColor?: string;
}) {
  return (
    <div style={stackedRowStyle}>
      <span style={stackedLabelStyle}>{label}</span>
      <span style={{ ...stackedValueStyle, color: valueColor ?? 'var(--fg-2)' }}>
        {displayDigits(value)}
        {unit && <span style={stackedUnitStyle}>{unit}</span>}
      </span>
    </div>
  );
}

const FATIGUE_COLORS = {
  fresh: 'var(--text-success)',
  normal: 'var(--text-warning)',
  high: 'var(--text-danger)',
} as const;

/**
 * Header strip with athlete FTP, weight, W/kg, max HR, fatigue, and goal race.
 */
export function AthleteMetricsBar({
  userId,
  athleteName,
  compact = false,
  inline = false,
  stacked = false,
  refreshToken = 0,
}: AthleteMetricsBarProps) {
  const { t } = useTranslation();
  const { weight, ftp, maxHr, wPerKg, fatigue, goalRace, loading } = useAthleteMetrics(userId, refreshToken);

  if (!userId) return null;

  const ftpText = ftp != null ? String(Math.round(ftp)) : '—';
  const weightText = weight != null ? weight.toFixed(weight % 1 === 0 ? 0 : 1) : '—';
  const wkgText = wPerKg != null ? wPerKg.toFixed(2) : '—';
  const maxHrText = maxHr != null ? String(Math.round(maxHr)) : '—';
  const fatigueText = fatigue ? t(`studio.fatigue.${fatigue === 'normal' ? 'optimal' : fatigue === 'high' ? 'tired' : 'fresh'}`) : '—';
  const fatigueColor = fatigue ? FATIGUE_COLORS[fatigue] : undefined;
  const goalRaceText = goalRace ?? '—';

  if (stacked) {
    return (
      <div style={stackedContainerStyle}>
        {athleteName && <div style={stackedNameStyle}>{athleteName}</div>}
        {loading && <span style={loadingStyle}><NmLoader size="sm" inline label={t('studio.metrics.loading')} /></span>}
        <StackedRow label={t('studio.metrics.ftp')} value={ftpText} unit={ftp != null ? t('studio.metrics.watt') : undefined} />
        <StackedRow label={t('studio.metrics.weight')} value={weightText} unit={weight != null ? t('studio.metrics.kg') : undefined} />
        <StackedRow label={t('studio.metrics.wkg')} value={wkgText} />
        <StackedRow label={t('studio.metrics.maxHr')} value={maxHrText} />
        <StackedRow label={t('studio.metrics.fatigue')} value={fatigueText} valueColor={fatigueColor} />
        <StackedRow label={t('studio.metrics.goalRace')} value={goalRaceText} />
      </div>
    );
  }

  const containerStyle: React.CSSProperties = inline
    ? { display: 'flex', alignItems: 'center', gap: compact ? 6 : 8, flexShrink: 0, flexWrap: 'wrap' }
    : {
      ...barStyle,
      padding: compact ? '5px 10px' : '6px 12px',
      gap: compact ? 8 : 10,
    };

  return (
    <div className="athlete-context-strip" style={containerStyle}>
      {athleteName && (
        <span style={{ ...nameStyle, fontSize: compact ? 10 : 11 }}>{athleteName}</span>
      )}
      {loading && <NmLoader size="xs" inline />}
      <MetricChip label={t('studio.metrics.ftp')} value={ftpText} unit={ftp != null ? t('studio.metrics.watt') : undefined} compact={compact} />
      <Sep inline={inline} compact={compact} />
      <MetricChip label={t('studio.metrics.weight')} value={weightText} unit={weight != null ? t('studio.metrics.kg') : undefined} compact={compact} />
      <Sep inline={inline} compact={compact} />
      <MetricChip label={t('studio.metrics.wkg')} value={wkgText} compact={compact} />
      <Sep inline={inline} compact={compact} />
      <MetricChip label={t('studio.metrics.maxHr')} value={maxHrText} compact={compact} />
      <Sep inline={inline} compact={compact} />
      <MetricChip
        label={t('studio.metrics.fatigue')}
        value={fatigueText}
        valueColor={fatigueColor}
        compact={compact}
      />
      <Sep inline={inline} compact={compact} />
      <MetricChip label={t('studio.metrics.goalRace')} value={goalRaceText} compact={compact} />
    </div>
  );
}

const barStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexShrink: 0,
  borderRadius: 'var(--r-2)',
  background: 'var(--surface-1)',
  boxShadow: 'var(--nm-flat)',
  border: '1px solid var(--surface-sunken)',
  flexWrap: 'wrap',
};

const nameStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--fg-2)',
  fontFamily: 'var(--font-display)',
  paddingRight: 4,
  borderRight: '1px solid var(--surface-sunken)',
  marginRight: 2,
};

const chipStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 6,
};

const chipLabel: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: 'var(--fg-4)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  fontFamily: 'var(--font-body)',
};

const chipValue: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 800,
  color: 'var(--fg-1)',
  fontFamily: 'var(--font-mono)',
  lineHeight: 1,
};

const chipUnit: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  color: 'var(--fg-3)',
  marginLeft: 3,
};

const sepStyle: React.CSSProperties = {
  color: 'var(--fg-4)',
  opacity: 0.45,
  fontSize: 12,
  userSelect: 'none',
};

const headerSepInline: React.CSSProperties = {
  color: 'var(--fg-4)',
  opacity: 0.35,
  fontSize: 13,
  fontWeight: 300,
  userSelect: 'none',
};

const loadingStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--color-accent)',
  animation: 'pulse 1s infinite',
};

const stackedContainerStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 7,
  marginTop: 12,
  paddingTop: 12,
  borderTop: '1px solid var(--surface-sunken)',
};

const stackedNameStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--fg-2)',
  marginBottom: 2,
};

const stackedRowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 3,
  minWidth: 0,
  padding: '7px 9px',
  borderRadius: 'var(--r-2)',
  border: '1px solid var(--surface-sunken)',
  background: 'var(--surface-1)',
  fontSize: 11,
  lineHeight: 1.35,
  direction: 'ltr',
  textAlign: 'left',
};

const stackedLabelStyle: React.CSSProperties = {
  maxWidth: '100%',
  fontSize: 9,
  fontWeight: 700,
  color: 'var(--fg-4)',
  letterSpacing: '0.025em',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const stackedValueStyle: React.CSSProperties = {
  maxWidth: '100%',
  fontSize: 13,
  fontWeight: 800,
  fontFamily: 'var(--font-mono)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const stackedUnitStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  color: 'var(--fg-3)',
  marginInlineStart: 3,
};
